import {
  NgOptimizedImage, isPlatformBrowser,
  NgTemplateOutlet,
  UpperCasePipe,
  TitleCasePipe,
  SlicePipe
} from '@angular/common';
import Swal from 'sweetalert2';
import {
  Component,
  HostListener,
  ElementRef,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
  signal,
  effect,
  OnDestroy,
  PLATFORM_ID,
  computed,
  ChangeDetectorRef,
  viewChild
} from '@angular/core';

import { trigger, transition, style, animate } from '@angular/animations';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, first } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuItem } from 'primeng/api';
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { MenuModule, Menu } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import { AuthService } from '../../services/auth/auth.service';
import { ProfileMenuService } from '../../services/profile/profile.service';
import { SearchService } from '../../services/search/search.service';
import { NavigationContextService } from '../../services/navigation-context/navigation-context.service';
import { SidebarService } from '../../services/sidebar/sidebar.service';
import { ToastrService } from '../../services/toastr/toastr.service';
import { LoginComponent } from 'src/app/module/auth/pages/login/login.component';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    FormsModule,
    RouterModule,
    NgOptimizedImage,
    MenuModule,
    InputTextModule,
    RippleModule,
    ButtonModule,
    TooltipModule,
    NgTemplateOutlet,
    SlicePipe,
    TitleCasePipe,
    UpperCasePipe
],
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-out', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0 })),
            ]),
        ]),
        trigger('slideDown', [
            transition(':enter', [
                style({ transform: 'translateY(-20px)', opacity: 0 }),
                animate('250ms ease-out', style({ transform: 'translateY(0)', opacity: 1 })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateY(-20px)', opacity: 0 })),
            ]),
        ]),
    ]
})
export class HeaderComponent implements OnDestroy {
  readonly mobileSearchInput = viewChild<ElementRef<HTMLInputElement>>('mobileSearchInput');
  readonly menu = viewChild<Menu>('menu');

  protected readonly user = computed(() => this.auth.userDetails());
  protected readonly isLogged = computed(() => this.auth.isLogged());
  protected readonly isChecking = computed(() => this.auth.isChecking());
  protected readonly isSearchEmpty = computed(() => !this.searchService.searchText().trim());
  protected readonly searchText = computed(() => this.searchService.searchText());

  /**
   * Botão do sidebar no header, de forma dinâmica e com fonte única:
   * - mobile: sempre visível quando logado (abre a gaveta);
   * - desktop: visível exatamente nas rotas em que o sidebar opera em modo
   *   push — a mesma regra do SidebarService.isPushMode(), sem duplicar
   *   listas de rotas aqui.
   */
  protected readonly shouldShowSidebarBtn = computed(() => {
    if (!this.isLogged()) {
      return false;
    }
    return this.isMobile() || this.sidebarService.isPushMode();
  });

  protected readonly isMobile = computed(() => this.sidebarService.isMobile());
  protected readonly isSidebarOpen = computed(() => this.sidebarService.isOpen());
  protected readonly isSearching = signal<boolean>(false);
  protected readonly profileOptions = signal<MenuItem[]>([]);
  protected readonly isSearchFocused = signal<boolean>(false);
  protected readonly isListening = signal<boolean>(false);
  protected readonly isScrolled = signal<boolean>(false);

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (isPlatformBrowser(this.platformId)) {
      this.isScrolled.set(window.scrollY > 10);
      const menu = this.menu();
      if (menu) {
        menu.hide();
      }
    }
  }

  private recognitionInstance: any = null;

  ref: DynamicDialogRef | undefined | null;

  // Propriedade intermediária para ngModel
  get searchTextValue(): string {
    return this.searchService.searchText();
  }

  set searchTextValue(value: string) {
    this.searchService.setSearchText(value);
  }

  // Injeção moderna
  public readonly auth = inject(AuthService);
  public readonly dialogService = inject(DialogService);
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly navigationContext = inject(NavigationContextService);
  public readonly profile = inject(ProfileMenuService);
  private readonly sidebarService = inject(SidebarService);
  private readonly toastr = inject(ToastrService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);


  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: any) => {
        const url = (event as NavigationEnd).urlAfterRedirects || (event as NavigationEnd).url;

        // Fora do modo push (gaveta mobile/overlay), navegar fecha o sidebar.
        // Em modo push o SidebarService mantém/reabre sozinho — fonte única.
        if (!this.sidebarService.isPushMode()) {
          this.sidebarService.close();
        }

        this.isSearching.set(false);

        if (!url?.includes('/filter')) {
          this.searchService.clearSearchText();
        }
      });

    effect(() => {
      const logged = this.auth.isLogged();
      this.cdr.markForCheck();

      if (logged) {
        // Sempre atualizar as opções do perfil quando logado
        this.profileOptions.set(this.profile.getProfileOptions() as MenuItem[]);
      } else {
        this.profileOptions.set([]);
      }
    });

    if (isPlatformBrowser(this.platformId)) {
      const storedSearchText = sessionStorage.getItem('searchText');
      if (storedSearchText) {
        this.searchService.setSearchText(storedSearchText);
      }
    }
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.isSearching() && !this.elementRef.nativeElement.contains(event.target)) {
      this.closeSearch();
    }
  }

  protected performSearch(): void {
    const term = this.searchService.searchText().trim();

    if (term) {
      this.isSearching.set(false);
      this.router.navigate(['/filter'], { queryParams: { q: term } });
    }
  }

  protected clearSearch(): void {
    this.searchService.clearSearchText();
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('searchText');
      localStorage.removeItem('searchText');
    }

    const currentUrl = this.router.url;
    if (currentUrl.includes('/filter')) {
      this.router.navigateByUrl(this.navigationContext.backTarget());
    }

    this.cdr.markForCheck();
  }

  protected toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  protected toggleSearch(): void {
    this.isSearching.update(v => !v);
    if (this.isSearching()) {
      setTimeout(() => {
        const input = this.mobileSearchInput()?.nativeElement;
        if (input) {
          input.focus();
        }
      }, 100);
    }
  }

  protected openModalLogin(event?: Event): void {
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }

    // Fechar modal anterior se existir
    if (this.ref) {
      this.ref.close();
      this.ref = null;
    }

    this.ref = this.dialogService.open(LoginComponent, {
      header: 'Login',
      width: 'min(92vw, 500px)',
      height: 'auto',
      maximizable: false,
      closable: true,
      closeOnEscape: true,
      dismissableMask: true,
      showHeader: true,
      modal: true,
      styleClass: 'login-modal-custom',
      style: {
        'max-width': '500px',
        'border-radius': '12px',
      },
      contentStyle: {
        overflow: 'hidden',
        display: 'flex',
        'flex-direction': 'column',
        'min-height': '0',
      },
      position: 'center',
      data: {},
    });

    this.ref?.onClose.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.ref = null;
    });
  }

  protected formatUserName(name: string): string {
    const parts = name?.split(' ') || [];
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[1]}`;
    }
    return parts[0] || '';
  }

  protected closeSearch(): void {
    this.isSearching.set(false);
  }

  protected onSearchFocus(): void {
    this.isSearchFocused.set(true);
  }

  protected onSearchBlur(): void {
    this.isSearchFocused.set(false);
  }

  protected startVoiceSearch(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.isListening()) {
      this.stopVoiceSearch();
      return;
    }

    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      this.recognitionInstance = new SpeechRecognition();

      this.recognitionInstance.lang = 'pt-BR';
      this.recognitionInstance.continuous = false;
      this.recognitionInstance.interimResults = false;
      this.recognitionInstance.maxAlternatives = 1;

      this.recognitionInstance.onstart = () => {
        this.isListening.set(true);
        this.cdr.markForCheck();
      };

      let recognizedText = '';

      this.recognitionInstance.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript.trim();
        if (transcript) {
          recognizedText = transcript;
          this.searchService.setSearchText(transcript);
          this.cdr.markForCheck();
        }
      };

      this.recognitionInstance.onend = () => {
        this.isListening.set(false);
        this.recognitionInstance = null;

        // Usar o texto reconhecido diretamente para evitar problemas de timing
        if (recognizedText) {
          // Garantir que o texto está no service
          this.searchService.setSearchText(recognizedText);
          // Navegar imediatamente
          this.performSearch();
        } else {
          // Fallback: tentar pegar do service
          setTimeout(() => {
            const searchText = this.searchService.searchText().trim();

            if (searchText) {
              this.performSearch();
            }
          }, 100);
        }

        this.cdr.markForCheck();
      };

      this.recognitionInstance.onerror = (event: any) => {
        console.error('Speech recognition error', event.error);
        this.isListening.set(false);
        this.recognitionInstance = null;
        this.cdr.markForCheck();

        if (event.error === 'no-speech') {
          this.searchService.setSearchText('');
        }
      };

      try {
        this.recognitionInstance.start();
      } catch (error) {
        console.error('Error starting speech recognition', error);
        this.isListening.set(false);
        this.recognitionInstance = null;
        this.cdr.markForCheck();
      }
    } else {
      Swal.fire({
        title: 'Funcionalidade Indisponível',
        text: 'A busca por voz não está disponível no seu navegador. Recomendamos o uso do Google Chrome, Edge ou Safari.',
        icon: 'info',
        confirmButtonText: 'Entendi',
        confirmButtonColor: '#F2994A'
      });
    }
  }

  protected stopVoiceSearch(): void {
    if (this.recognitionInstance) {
      try {
        this.recognitionInstance.stop();
      } catch (error) {
        console.error('Error stopping speech recognition', error);
      }
      this.recognitionInstance = null;
      this.isListening.set(false);
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    if (this.ref) {
      this.ref.close();
    }
    this.stopVoiceSearch();
  }
}
